import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import ModalComponent from 'components/Common/Modal';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import useLobbyingMaterial from 'api/hooks/useLobbyingMaterial';
import type { TeamLobbyingMaterialItem } from 'api/hooks/useLobbyingMaterial';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import { lobbyingMaterialEditPath } from 'components/UploadHub/UploadHubPreviewPage/lobbyingMaterialEditPath';

const AddToLobbyingMaterialButton = ({
  file,
  setIsMoreActionsOpen,
}: {
  file: UploadHubListItem;
  setIsMoreActionsOpen: (value: boolean) => void;
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const savedFiles = [{ url: file.url, name: file.fileName }];

  const goToNew = () => {
    navigate(
      { pathname: '/brief-policymakers/add-content', search: '?step=1' },
      { state: { headline: file.fileName, attachments: { savedFiles } } }
    );
  };

  const goToExisting = (item: TeamLobbyingMaterialItem) => {
    navigate(lobbyingMaterialEditPath(item.type, item.id), { state: { attachments: { savedFiles } } });
  };

  return (
    <>
      <button
        className='w-100 d-flex align-items-center text-left general-button list-action-button specific-right-padding-28 pl-3 py-2'
        onClick={() => {
          setIsOpen(true);
          setIsMoreActionsOpen(false);
        }}
      >
        <span
          className='political-updates-nav-icon specific-left-margin-2 icon-briefing-material paragraph-p1 d-inline-block-centered specific-top-margin-1'
          style={{ marginRight: '-2px' }}
        />{' '}
        <span>Add to lobbying material</span>
      </button>
      <ModalComponent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        overlayClassName='react-modal-overlay adjustment-of-position'
      >
        {isOpen && (
          <ModalContent
            fileName={file.fileName}
            setIsOpen={setIsOpen}
            onConfirmNew={goToNew}
            onConfirmExisting={goToExisting}
          />
        )}
      </ModalComponent>
    </>
  );
};

interface ModalContentProps {
  fileName: string;
  setIsOpen: (value: boolean) => void;
  onConfirmNew: () => void;
  onConfirmExisting: (item: TeamLobbyingMaterialItem) => void;
}

const ModalContent = ({ fileName, setIsOpen, onConfirmNew, onConfirmExisting }: ModalContentProps) => {
  const { getTeamLobbyingMaterial } = useLobbyingMaterial();
  const [option, setOption] = useState<'new' | 'existing'>('new');
  const [items, setItems] = useState<TeamLobbyingMaterialItem[]>([]);
  const [selected, setSelected] = useState<TeamLobbyingMaterialItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchItems = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  fetchItems.current = async (signal) => {
    try {
      const results = await getTeamLobbyingMaterial({ signal });
      if (results) {
        setItems(results);
        setSelected(results[0] ?? null);
      }
      setIsLoading(false);
    } catch {
      // apiClient's error interceptor already shows a toast
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchItems.current?.(controller.signal);
    return () => controller.abort('Lobbying material list cancelled.');
  }, []);

  const confirm = () => {
    if (option === 'new') onConfirmNew();
    else if (selected) onConfirmExisting(selected);
    setIsOpen(false);
  };

  return (
    <>
      <h3 className='p-4 border-bottom title-h4-bold mb-0'>Add “{fileName}” to a lobbying material</h3>
      <div className='p-4'>
        <div className='flex-centered gap-10 mb-4'>
          <button
            className={`text-left general-button col border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
              option === 'new' ? 'selected' : ''
            }`}
            onClick={() => setOption('new')}
          >
            <span className='select-item d-inline-block-centered mr-2' />A new lobbying material
          </button>
          <button
            className={`text-left general-button col border rounded specific-x-padding-20 specific-y-padding-10 hovered-button button-radio-box ${
              option === 'existing' ? 'selected' : ''
            }`}
            onClick={() => setOption('existing')}
          >
            <span className='select-item d-inline-block-centered mr-2' />
            An existing lobbying material
          </button>
        </div>

        {option === 'existing' && (
          <div className='mb-4'>
            <DropdownComponent
              mainItem={() => (
                <span className='specific-y-padding-5 d-inline-block' style={{ maxHeight: '47px' }}>
                  {isLoading ? 'Loading…' : selected ? selected.title : 'Select a lobbying material'}
                </span>
              )}
            >
              <CustomScrollbar
                className='simple-scrollbar'
                autoHeight
                autoHeightMin='100%'
                autoHeightMax='226px'
                maximalThumbYSize={100}
              >
                {isLoading ? (
                  <div className='p-3 light-60-text font-italic'>Loading…</div>
                ) : items.length === 0 ? (
                  <div className='p-3 light-60-text font-italic'>You have not created any lobbying material yet</div>
                ) : (
                  <ul className='policy-list px-0 py-1 mb-0'>
                    {items.map((item) => (
                      <li
                        key={`lobbying-material-${item.id}`}
                        onClick={() => setSelected(item)}
                        className='flex-centered specific-y-padding-8 px-3 list-action-button pointer'
                      >
                        <span className='flex-grow-1'>{item.title}</span>
                        {selected?.id === item.id && <span className='icon-tick paragraph-p4' />}
                      </li>
                    ))}
                  </ul>
                )}
              </CustomScrollbar>
            </DropdownComponent>
          </div>
        )}

        <div className='pt-3 d-flex justify-content-end'>
          <button
            onClick={confirm}
            disabled={option === 'existing' && !selected}
            className='general-button rounded-pill action-button px-5 specific-y-padding-8'
          >
            {option === 'new' ? 'Create lobbying material' : 'Add to lobbying material'}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddToLobbyingMaterialButton;
